import React, { useState } from 'react';
import { CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { translateWithLanguage } from '../../i18n/i18n';

const QCMComponent = ({ qcm, language = 'fr' }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const resetLabel = translateWithLanguage(language, 'qcm.reset');
  const showResultsLabel = translateWithLanguage(language, 'qcm.showResults');
  const hideResultsLabel = translateWithLanguage(language, 'qcm.hideResults');
  const goodAnswerLabel = translateWithLanguage(language, 'qcm.goodAnswer');
  const wrongAnswerLabel = translateWithLanguage(language, 'qcm.wrongAnswer');
  const finalScoreTitleExcellent = translateWithLanguage(language, 'qcm.finalScoreTitleExcellent');
  const finalScoreTitleGood = translateWithLanguage(language, 'qcm.finalScoreTitleGood');
  const finalScoreTitleRetry = translateWithLanguage(language, 'qcm.finalScoreTitleRetry');
  const finalScoreLabel = translateWithLanguage(language, 'qcm.finalScoreLabel');
  const finalScoreTextExcellent = translateWithLanguage(language, 'qcm.finalScoreTextExcellent');
  const finalScoreTextGood = translateWithLanguage(language, 'qcm.finalScoreTextGood');
  const finalScoreTextRetry = translateWithLanguage(language, 'qcm.finalScoreTextRetry');

  const handleAnswerSelect = (questionId, responseId) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: responseId
    }));
  };

  const calculateScore = () => {
    let correctAnswers = 0;
    let totalQuestions = qcm.questions.length;

    qcm.questions.forEach(question => {
      const selectedResponse = selectedAnswers[question.id];
      if (selectedResponse) {
        const response = question.responses.find(r => r.id === selectedResponse);
        if (response && response.is_correct) correctAnswers++;
      }
    });

    return { correctAnswers, totalQuestions, percentage: Math.round((correctAnswers / totalQuestions) * 100) };
  };

  const resetQuiz = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };

  const score = calculateScore();

  return (
    <div className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 px-3 py-4 sm:p-6 my-6">
      {/* === HEADER (inchangé sur toutes les tailles) === */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
            {qcm.title}
          </h3>
          {qcm.description && (
            <p className="text-blue-700 text-sm mt-1">{qcm.description}</p>
          )}
          <p className="text-blue-600 text-xs mt-1">
            {qcm.question_count} question{qcm.question_count > 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {showResults && (
            <div className="bg-white px-3 py-2 sm:py-1 rounded-full border border-blue-300 text-center">
              <span className="text-blue-800 font-semibold">
                {score.correctAnswers}/{score.totalQuestions} ({score.percentage}%)
              </span>
            </div>
          )}
          
          <button
            onClick={resetQuiz}
            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 sm:py-1 rounded-md text-sm flex items-center justify-center gap-1 transition-colors"
          >
            <RotateCcw size={14} />
            {resetLabel}
          </button>
          
          <button
            onClick={() => setShowResults(!showResults)}
            className={`px-3 py-2 sm:py-1 rounded-md text-sm font-medium transition-colors text-center ${
              showResults ? 'bg-gray-500 hover:bg-gray-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {showResults ? hideResultsLabel : showResultsLabel}
          </button>
        </div>
      </div>

      {/* === QUESTIONS === */}
      <div className="space-y-6">
        {qcm.questions.map((question, questionIndex) => (
          <div key={question.id} className="bg-white rounded-lg border border-blue-100 p-1 sm:p-4">
            <div className="flex items-start gap-1 sm:gap-3">
              <div className="bg-blue-100 text-blue-800 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0 mt-0.5">
                {questionIndex + 1}
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-800 mb-3">{question.text}</h4>

                {/* === VERSION MOBILE (ultra-compact) === */}
                <div className="sm:hidden space-y-0">
                  {question.responses.map(response => {
                    const isSelected = selectedAnswers[question.id] === response.id;
                    const showCorrectness = showResults && isSelected;
                    const isCorrect = response.is_correct;

                    return (
                      <div
                        key={response.id}
                        onClick={() => !showResults && handleAnswerSelect(question.id, response.id)}
                        className={`
                          px-4 py-3 border-t border-gray-100 cursor-pointer
                          ${isSelected 
                            ? showCorrectness 
                              ? isCorrect ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'
                              : 'bg-blue-50'
                            : showResults && isCorrect ? 'bg-green-50' : 'hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`
                              w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center
                              ${isSelected 
                                ? showCorrectness 
                                  ? isCorrect ? 'bg-green-500 border-green-500' : 'bg-red-500 border-red-500'
                                  : 'bg-blue-500 border-blue-500'
                                : showResults && isCorrect ? 'bg-green-500 border-green-500' : 'border-gray-400'
                            }`}>
                              {(isSelected || (showResults && isCorrect)) && <div className="w-2.5 h-2.5 bg-white rounded-full"></div>}
                            </div>
                            <span className={`text-sm ${showCorrectness ? (isCorrect ? 'text-green-800 font-semibold' : 'text-red-800') : showResults && isCorrect ? 'text-green-800 font-semibold' : 'text-gray-700'}`}>
                              {response.text}
                            </span>
                          </div>
                          {showCorrectness && (isCorrect ? <CheckCircle className="text-green-600" size={22} /> : <XCircle className="text-red-600" size={22} />)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* === VERSION DESKTOP / TABLETTE (ta version d’origine 100% intacte) === */}
                <div className="hidden sm:block space-y-2">
                  {question.responses.map((response) => {
                    const isSelected = selectedAnswers[question.id] === response.id;
                    const showCorrectness = showResults && isSelected;

                    return (
                      <div
                        key={response.id}
                        onClick={() => !showResults && handleAnswerSelect(question.id, response.id)}
                        className={`py-1 px-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? showCorrectness
                              ? response.is_correct
                                ? 'border-green-500 bg-green-50'
                                : 'border-red-500 bg-red-50'
                              : 'border-blue-500 bg-blue-50'
                            : showResults && response.is_correct
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className={`w-6 h-6 min-w-[24px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected
                                ? showCorrectness
                                  ? response.is_correct ? 'border-green-500 bg-green-500' : 'border-red-500 bg-red-500'
                                  : 'border-blue-500 bg-blue-500'
                                : showResults && response.is_correct
                                  ? 'border-green-500 bg-green-500'
                                  : 'border-gray-300'
                            }`}>
                              {isSelected && <div className="w-3 h-3 rounded-full bg-white"></div>}
                              {showResults && response.is_correct && !isSelected && <CheckCircle size={14} className="text-white" />}
                            </div>
                            <span className={`text-sm sm:text-base ${showCorrectness
                                ? response.is_correct ? 'text-green-800 font-medium' : 'text-red-800'
                                : showResults && response.is_correct ? 'text-green-800 font-medium' : 'text-gray-700'
                            }`}>
                              {response.text}
                            </span>
                          </div>
                          {showCorrectness && (response.is_correct ? <CheckCircle size={20} className="text-green-500" /> : <XCircle size={20} className="text-red-500" />)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Feedback (identique partout) */}
                {showResults && selectedAnswers[question.id] && (
                  <div className={`mt-3 p-2 rounded-md text-sm ${
                    question.responses.find(r => r.id === selectedAnswers[question.id])?.is_correct
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {question.responses.find(r => r.id === selectedAnswers[question.id])?.is_correct
                      ? goodAnswerLabel
                      : wrongAnswerLabel
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Score final (inchangé) */}
      {showResults && (
        <div className={`mt-6 p-4 rounded-lg border-2 text-center ${
          score.percentage >= 70 ? 'border-green-300 bg-green-50'
          : score.percentage >= 50 ? 'border-yellow-300 bg-yellow-50'
          : 'border-red-300 bg-red-50'
        }`}>
          <div className="text-2xl font-bold mb-2">
            {score.percentage >= 70 ? finalScoreTitleExcellent : score.percentage >= 50 ? finalScoreTitleGood : finalScoreTitleRetry}
          </div>
          <h4 className="text-lg font-semibold mb-1">
            {finalScoreLabel} {score.correctAnswers}/{score.totalQuestions} ({score.percentage}%)
          </h4>
          <p className="text-sm">
            {score.percentage >= 70
              ? finalScoreTextExcellent
              : score.percentage >= 50
              ? finalScoreTextGood
              : finalScoreTextRetry
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default QCMComponent;